import * as React from 'react';
import { Plus, Star, Trash2 } from 'lucide-react';
import {
  CONTACT_CHANNEL_KINDS,
  CONTACT_CHANNEL_KIND_LABELS,
  type ContactChannelInput,
  type ContactChannelKind,
} from '@aimmo/contracts';
import { Button, Input, cn } from '@aimmo/ui';

const PLACEHOLDERS: Record<ContactChannelKind, string> = {
  email: 'nome@example.it',
  phone: '02 1234567',
  mobile: '335 1234567',
};

export interface ContactChannelsFieldProps {
  value: unknown;
  onChange: (value: ContactChannelInput[]) => void;
  disabled: boolean;
}

/**
 * The recapiti, as three fixed lists (U4).
 *
 * The type is the block a value was typed into, never a dropdown: asking
 * someone to classify a phone number they are copying off a business card is
 * work the form can do for them. Each block keeps its own primary — the one the
 * list column shows — and the first entry claims it unless another is starred.
 */
export function ContactChannelsField({ value, onChange, disabled }: ContactChannelsFieldProps) {
  const channels = React.useMemo(
    () => (Array.isArray(value) ? (value as ContactChannelInput[]) : []),
    [value],
  );

  function update(next: ContactChannelInput[]): void {
    onChange(next);
  }

  function add(kind: ContactChannelKind): void {
    update([...channels, { kind, value: '', isPrimary: !channels.some((c) => c.kind === kind) }]);
  }

  function edit(index: number, patch: Partial<ContactChannelInput>): void {
    update(channels.map((channel, i) => (i === index ? { ...channel, ...patch } : channel)));
  }

  function remove(index: number): void {
    const dropped = channels[index];
    const rest = channels.filter((_, i) => i !== index);

    // Removing the primary must leave the block with one, or the read model
    // picks whichever row the database happens to return first.
    if (dropped?.isPrimary) {
      const heir = rest.findIndex((channel) => channel.kind === dropped.kind);
      if (heir >= 0) rest[heir] = { ...rest[heir]!, isPrimary: true };
    }
    update(rest);
  }

  function makePrimary(index: number): void {
    const target = channels[index];
    if (!target) return;
    update(
      channels.map((channel, i) =>
        channel.kind === target.kind ? { ...channel, isPrimary: i === index } : channel,
      ),
    );
  }

  return (
    <div className="grid gap-4" data-slot="contact-channels">
      {CONTACT_CHANNEL_KINDS.map((kind) => {
        const rows = channels
          .map((channel, index) => ({ channel, index }))
          .filter((entry) => entry.channel.kind === kind);

        return (
          <fieldset key={kind} className="grid gap-2">
            <legend className="text-label uppercase tracking-[0.08em] text-faint">
              {CONTACT_CHANNEL_KIND_LABELS[kind]}
            </legend>

            {rows.map(({ channel, index }, position) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <Input
                  className="min-w-48 flex-1"
                  type={kind === 'email' ? 'email' : 'tel'}
                  inputMode={kind === 'email' ? 'email' : 'tel'}
                  placeholder={PLACEHOLDERS[kind]}
                  aria-label={`${CONTACT_CHANNEL_KIND_LABELS[kind]} ${position + 1}`}
                  disabled={disabled}
                  value={channel.value}
                  onChange={(event) => edit(index, { value: event.target.value })}
                />
                <Input
                  className="w-32"
                  placeholder="etichetta"
                  aria-label="Etichetta"
                  disabled={disabled}
                  value={channel.label ?? ''}
                  onChange={(event) => edit(index, { label: event.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Rendi principale"
                  aria-pressed={channel.isPrimary === true}
                  disabled={disabled || rows.length < 2}
                  onClick={() => makePrimary(index)}
                >
                  <Star className={cn('size-4', channel.isPrimary && 'fill-accent text-accent')} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Rimuovi recapito"
                  disabled={disabled}
                  onClick={() => remove(index)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}

            <div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => add(kind)}
              >
                <Plus />
                {`Aggiungi ${CONTACT_CHANNEL_KIND_LABELS[kind].toLowerCase()}`}
              </Button>
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
